import { baseApi as api } from './api.base.js';
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    userTenantsControllerAddUserMembershipV1: build.mutation<
      UserTenantsControllerAddUserMembershipV1ApiResponse,
      UserTenantsControllerAddUserMembershipV1ApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/admin/users/${queryArg.userId}/tenants`,
        method: 'POST',
        body: queryArg.addUserMembershipBody,
      }),
    }),
    userTenantsControllerListUserMembershipsV1: build.query<
      UserTenantsControllerListUserMembershipsV1ApiResponse,
      UserTenantsControllerListUserMembershipsV1ApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/admin/users/${queryArg.userId}/tenants`,
      }),
    }),
    userTenantsControllerGetUserMembershipV1: build.query<
      UserTenantsControllerGetUserMembershipV1ApiResponse,
      UserTenantsControllerGetUserMembershipV1ApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/admin/users/${queryArg.userId}/tenants/${queryArg.tenantId}`,
      }),
    }),
    userTenantsControllerRemoveUserFromTenantV1: build.mutation<
      UserTenantsControllerRemoveUserFromTenantV1ApiResponse,
      UserTenantsControllerRemoveUserFromTenantV1ApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/admin/users/${queryArg.userId}/tenants/${queryArg.tenantId}`,
        method: 'DELETE',
      }),
    }),
    userTenantsControllerUpdateUserMembershipV1: build.mutation<
      UserTenantsControllerUpdateUserMembershipV1ApiResponse,
      UserTenantsControllerUpdateUserMembershipV1ApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/admin/users/${queryArg.userId}/memberships/${queryArg.membershipId}`,
        method: 'PUT',
        body: queryArg.updateUserMembershipBody,
      }),
    }),
    userTenantsControllerRemoveUserMembershipV1: build.mutation<
      UserTenantsControllerRemoveUserMembershipV1ApiResponse,
      UserTenantsControllerRemoveUserMembershipV1ApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/admin/users/${queryArg.userId}/memberships/${queryArg.membershipId}`,
        method: 'DELETE',
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as generatedApi };
export type UserTenantsControllerAddUserMembershipV1ApiResponse =
  /** status 200 Returns created tenant membership */ AddUserMembershipResponse;
export type UserTenantsControllerAddUserMembershipV1ApiArg = {
  userId: number;
  addUserMembershipBody: TenantMembershipCreateForUser;
};
export type UserTenantsControllerListUserMembershipsV1ApiResponse =
  /** status 200 Returns the list of tenant memberships for the user */ ListUserMembershipsResponse;
export type UserTenantsControllerListUserMembershipsV1ApiArg = {
  userId: number;
};
export type UserTenantsControllerGetUserMembershipV1ApiResponse =
  /** status 200 Membership details for user in tenant */ GetUserMembershipResponse;
export type UserTenantsControllerGetUserMembershipV1ApiArg = {
  userId: number;
  tenantId: number;
};
export type UserTenantsControllerRemoveUserFromTenantV1ApiResponse =
  /** status 200 Returns the count of deleted memberships */ RemoveUserFromTenantResponse;
export type UserTenantsControllerRemoveUserFromTenantV1ApiArg = {
  userId: number;
  tenantId: number;
};
export type UserTenantsControllerUpdateUserMembershipV1ApiResponse =
  /** status 200 Returns updated tenant membership */ UpdateUserMembershipResponse;
export type UserTenantsControllerUpdateUserMembershipV1ApiArg = {
  userId: number;
  membershipId: number;
  updateUserMembershipBody: TenantMembershipUpdate;
};
export type UserTenantsControllerRemoveUserMembershipV1ApiResponse =
  /** status 200 Returns deleted tenant membership */ RemoveUserMembershipResponse;
export type UserTenantsControllerRemoveUserMembershipV1ApiArg = {
  userId: number;
  membershipId: number;
};
export type AddUserMembershipResponse = {
  /** Tenant membership with populated relations */
  data: {
    /** Tenant membership ID */
    id: number;
    /** User ID */
    userId: number;
    /** Tenant ID */
    tenantId: number;
    /** Role ID */
    roleId: number;
    /** Permission context/scope: "*" for tenant-wide, "resource_type" for all resources, "resource_type:id" for specific resource */
    context: string;
    user?: {
      /** User ID */
      id: number;
      /** User email address */
      email: string;
      /** Full name */
      fullname: string;
      /** Company name */
      company: string;
    };
    tenant?: {
      /** Tenant ID */
      id: number;
      /** Tenant name */
      name: string;
      /** Tenant slug (unique identifier, must contain at least one letter) */
      slug: string;
    };
    role?: {
      /** Role ID */
      id: number;
      /** Role name */
      name: string;
      /** Optional description for the role */
      description: null | string;
    };
    /** Creation timestamp */
    createdAt: string;
    /** Last update timestamp */
    updatedAt: string;
  };
};
export type TenantMembershipCreateForUser = {
  /** Tenant ID to add user to */
  tenantId?: number;
  /** Tenant slug to add user to (alternative to tenantId) */
  tenantSlug?: string;
  /** Role ID to assign */
  roleId: number;
  /** Permission context/scope: "*" for tenant-wide, "resource_type" for all resources, "resource_type:id" for specific resource */
  context?: string;
};
export type ListUserMembershipsResponse = {
  data: {
    /** Tenant membership ID */
    id: number;
    /** User ID */
    userId: number;
    /** Tenant ID */
    tenantId: number;
    /** Role ID */
    roleId: number;
    /** Permission context/scope: "*" for tenant-wide, "resource_type" for all resources, "resource_type:id" for specific resource */
    context: string;
    user?: {
      /** User ID */
      id: number;
      /** User email address */
      email: string;
      /** Full name */
      fullname: string;
      /** Company name */
      company: string;
    };
    tenant?: {
      /** Tenant ID */
      id: number;
      /** Tenant name */
      name: string;
      /** Tenant slug (unique identifier, must contain at least one letter) */
      slug: string;
    };
    role?: {
      /** Role ID */
      id: number;
      /** Role name */
      name: string;
      /** Optional description for the role */
      description: null | string;
    };
    /** Creation timestamp */
    createdAt: string;
    /** Last update timestamp */
    updatedAt: string;
  }[];
  meta: {
    total: number;
  };
};
export type GetUserMembershipResponse = {
  data: {
    /** Tenant membership ID */
    id: number;
    /** User ID */
    userId: number;
    /** Tenant ID */
    tenantId: number;
    /** Role ID */
    roleId: number;
    /** Permission context/scope: "*" for tenant-wide, "resource_type" for all resources, "resource_type:id" for specific resource */
    context: string;
    user?: {
      /** User ID */
      id: number;
      /** User email address */
      email: string;
      /** Full name */
      fullname: string;
      /** Company name */
      company: string;
    };
    tenant?: {
      /** Tenant ID */
      id: number;
      /** Tenant name */
      name: string;
      /** Tenant slug (unique identifier, must contain at least one letter) */
      slug: string;
    };
    role?: {
      /** Role ID */
      id: number;
      /** Role name */
      name: string;
      /** Optional description for the role */
      description: null | string;
    };
    /** Creation timestamp */
    createdAt: string;
    /** Last update timestamp */
    updatedAt: string;
  }[];
  meta: {
    total: number;
  };
};
export type RemoveUserFromTenantResponse = {
  data: {
    count: number;
  };
};
export type UpdateUserMembershipResponse = {
  /** Tenant membership with populated relations */
  data: {
    /** Tenant membership ID */
    id: number;
    /** User ID */
    userId: number;
    /** Tenant ID */
    tenantId: number;
    /** Role ID */
    roleId: number;
    /** Permission context/scope: "*" for tenant-wide, "resource_type" for all resources, "resource_type:id" for specific resource */
    context: string;
    user?: {
      /** User ID */
      id: number;
      /** User email address */
      email: string;
      /** Full name */
      fullname: string;
      /** Company name */
      company: string;
    };
    tenant?: {
      /** Tenant ID */
      id: number;
      /** Tenant name */
      name: string;
      /** Tenant slug (unique identifier, must contain at least one letter) */
      slug: string;
    };
    role?: {
      /** Role ID */
      id: number;
      /** Role name */
      name: string;
      /** Optional description for the role */
      description: null | string;
    };
    /** Creation timestamp */
    createdAt: string;
    /** Last update timestamp */
    updatedAt: string;
  };
};
export type TenantMembershipUpdate = {
  /** New role ID to assign */
  roleId?: number;
  /** Permission context/scope: "*" for tenant-wide, "resource_type" for all resources, "resource_type:id" for specific resource */
  context?: string;
};
export type RemoveUserMembershipResponse = {
  /** Tenant membership with populated relations */
  data: {
    /** Tenant membership ID */
    id: number;
    /** User ID */
    userId: number;
    /** Tenant ID */
    tenantId: number;
    /** Role ID */
    roleId: number;
    /** Permission context/scope: "*" for tenant-wide, "resource_type" for all resources, "resource_type:id" for specific resource */
    context: string;
    user?: {
      /** User ID */
      id: number;
      /** User email address */
      email: string;
      /** Full name */
      fullname: string;
      /** Company name */
      company: string;
    };
    tenant?: {
      /** Tenant ID */
      id: number;
      /** Tenant name */
      name: string;
      /** Tenant slug (unique identifier, must contain at least one letter) */
      slug: string;
    };
    role?: {
      /** Role ID */
      id: number;
      /** Role name */
      name: string;
      /** Optional description for the role */
      description: null | string;
    };
    /** Creation timestamp */
    createdAt: string;
    /** Last update timestamp */
    updatedAt: string;
  };
};
